<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { RouteName } from '@/router'
import { useAppConfig } from '@/composables/useAppConfig'
import { useAuthStore } from '@/stores/auth'
import { useAuth } from '@/composables/useAuth'
import { useLocale } from '@/composables/useLocale'
import ThemeToggle from '@/components/ui/ThemeToggle.vue'
import LanguageSwitcher from '@/components/ui/LanguageSwitcher.vue'
import AppLogo from '@/components/ui/AppLogo.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import BaseButton from '@/components/ui/BaseButton.vue'

interface Props {
  collapsed?: boolean
  mobile?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false,
  mobile: false,
})

const emit = defineEmits<{
  toggle: []
  close: []
}>()

const router = useRouter()
const { config } = useAppConfig()
const authStore = useAuthStore()
const { hasRole } = useAuth()
const { t } = useLocale()

const userMenuOpen = ref(false)

/**
 * The admin panel is an external EasyAdmin app on its own host, so it is a
 * plain link rather than a route. Hidden unless the API reported ROLE_ADMIN
 * for the current user; the panel enforces the same role server-side.
 */
const showAdminLink = computed(() => hasRole('ROLE_ADMIN') && Boolean(config.adminUrl))

interface NavigationItemConfig {
  labelKey: string
  icon: string
  route: RouteName
  requiresAuth?: boolean
  section?: 'main' | 'account'
}

const navigationItemsConfig: NavigationItemConfig[] = [
  {
    labelKey: 'common.navigation.dashboard',
    icon: 'solar:home-2-bold',
    route: RouteName.Dashboard,
    requiresAuth: true,
    section: 'main',
  },
  {
    labelKey: 'common.navigation.billing',
    icon: 'solar:card-bold',
    route: RouteName.BillingSubscription,
    requiresAuth: true,
    section: 'main',
  },
  {
    labelKey: 'common.navigation.chat',
    icon: 'solar:chat-round-dots-bold',
    route: RouteName.Chat,
    requiresAuth: true,
    section: 'main',
  },
]

const accountItemsConfig: NavigationItemConfig[] = [
  {
    labelKey: 'common.navigation.profile',
    icon: 'solar:user-bold',
    route: RouteName.Profile,
    requiresAuth: true,
    section: 'account',
  },
  {
    labelKey: 'common.navigation.settings',
    icon: 'solar:settings-bold',
    route: RouteName.Settings,
    requiresAuth: true,
    section: 'account',
  },
]

const navigationItems = computed(() =>
  navigationItemsConfig.map((item) => ({
    label: t(item.labelKey),
    icon: item.icon,
    route: item.route,
    requiresAuth: item.requiresAuth,
    section: item.section,
  }))
)

const accountItems = computed(() =>
  accountItemsConfig.map((item) => ({
    label: t(item.labelKey),
    icon: item.icon,
    route: item.route,
    requiresAuth: item.requiresAuth,
    section: item.section,
  }))
)

const currentRoute = computed(() => router.currentRoute.value.name)

const isActive = (routeName: RouteName): boolean => {
  return currentRoute.value === routeName
}

const toggleCollapsed = () => {
  emit('toggle')
}

const navigateTo = (routeName: RouteName) => {
  router.push({ name: routeName })
  if (props.mobile) {
    emit('close')
  }
}

const getUserInitials = (): string => {
  if (!authStore.user) return '?'

  if (authStore.user.name) {
    const names = authStore.user.name.split(' ')
    if (names.length >= 2 && names[0] && names[1]) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase()
    }
    return authStore.user.name.substring(0, 2).toUpperCase()
  }

  return authStore.user.email.substring(0, 2).toUpperCase()
}

const handleLogout = async () => {
  await authStore.logout()
  router.push({ name: RouteName.AuthLogin })
}

const toggleUserMenu = () => {
  userMenuOpen.value = !userMenuOpen.value
}

const currentYear = new Date().getFullYear()
</script>

<template>
  <aside
    :class="[
      'tw-flex tw-flex-col tw-bg-secondary-800 tw-text-white tw-h-full tw-transition-all tw-duration-300',
      props.collapsed && !props.mobile ? 'tw-w-16' : 'tw-w-64',
      props.mobile ? 'tw-fixed tw-inset-y-0 tw-left-0 tw-z-40' : '',
    ]"
    data-testid="app-sidebar"
  >
    <!-- Logo section -->
    <div
      :class="[
        'tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-border-secondary-700',
        props.collapsed && !props.mobile ? 'tw-justify-center' : '',
      ]"
    >
      <BaseButton
        v-if="!props.collapsed || props.mobile"
        type="button"
        variant="bare"
        class="hover:tw-opacity-80 tw-transition-opacity"
        @click="navigateTo(RouteName.Dashboard)"
      >
        <span class="tw-flex tw-items-center tw-gap-2">
          <AppLogo size="md" />
          <span class="tw-font-bold tw-text-lg">{{ config.name }}</span>
        </span>
      </BaseButton>
      <BaseButton
        v-else
        type="button"
        variant="bare"
        class="hover:tw-opacity-80 tw-transition-opacity"
        @click="navigateTo(RouteName.Dashboard)"
      >
        <AppLogo size="md" />
      </BaseButton>

      <BaseButton
        v-if="!props.mobile && !props.collapsed"
        type="button"
        variant="bare"
        class="tw-p-2 tw-rounded-md hover:tw-bg-secondary-700 tw-transition-colors"
        :aria-label="t('common.navigation.collapseSidebar')"
        data-testid="sidebar-collapse-button"
        @click="toggleCollapsed"
      >
        <AppIcon name="solar:alt-arrow-left-bold" />
      </BaseButton>

      <BaseButton
        v-if="props.mobile"
        type="button"
        variant="bare"
        class="tw-p-2 tw-rounded-md hover:tw-bg-secondary-700 tw-transition-colors"
        :aria-label="t('common.navigation.closeSidebar')"
        data-testid="sidebar-close-button"
        @click="emit('close')"
      >
        <AppIcon name="solar:close-square-bold" />
      </BaseButton>
    </div>

    <!-- Expand button when collapsed -->
    <BaseButton
      v-if="props.collapsed && !props.mobile"
      type="button"
      variant="bare"
      class="tw-p-2 tw-mx-auto tw-my-2 tw-rounded-md hover:tw-bg-secondary-700 tw-transition-colors"
      :aria-label="t('common.navigation.expandSidebar')"
      data-testid="sidebar-expand-button"
      @click="toggleCollapsed"
    >
      <AppIcon name="solar:alt-arrow-right-bold" />
    </BaseButton>

    <!-- Navigation -->
    <nav class="tw-flex-1 tw-overflow-y-auto tw-p-4" aria-label="Main navigation">
      <!-- Main navigation items -->
      <div class="tw-mb-6">
        <h3
          v-if="!props.collapsed || props.mobile"
          class="tw-text-xs tw-font-semibold tw-text-secondary-400 tw-uppercase tw-tracking-wider tw-mb-3 tw-px-3"
        >
          {{ t('common.navigation.mainMenu') }}
        </h3>
        <ul class="tw-space-y-1">
          <li v-for="item in navigationItems" :key="item.route + item.label">
            <BaseButton
              type="button"
              variant="bare"
              full-width
              :class="[
                'tw-px-3 tw-py-2.5 tw-rounded-lg tw-transition-all tw-duration-200',
                isActive(item.route)
                  ? 'tw-bg-primary-600 tw-text-white tw-font-medium tw-shadow-sm'
                  : 'tw-text-secondary-300 hover:tw-bg-secondary-700 hover:tw-text-white',
              ]"
              :title="props.collapsed && !props.mobile ? item.label : undefined"
              :data-testid="`nav-${item.label.toLowerCase()}`"
              @click="navigateTo(item.route)"
            >
              <span
                class="tw-flex tw-w-full tw-items-center tw-gap-3"
                :class="props.collapsed && !props.mobile ? 'tw-justify-center' : ''"
              >
                <AppIcon :name="item.icon" />
                <span
                  v-if="!props.collapsed || props.mobile"
                  class="tw-text-sm"
                >
                  {{ item.label }}
                </span>
              </span>
            </BaseButton>
          </li>
        </ul>
      </div>

      <!-- Account section -->
      <div>
        <h3
          v-if="!props.collapsed || props.mobile"
          class="tw-text-xs tw-font-semibold tw-text-secondary-400 tw-uppercase tw-tracking-wider tw-mb-3 tw-px-3"
        >
          {{ t('common.navigation.account') }}
        </h3>
        <ul class="tw-space-y-1">
          <li v-for="item in accountItems" :key="item.route + item.label">
            <BaseButton
              type="button"
              variant="bare"
              full-width
              :class="[
                'tw-px-3 tw-py-2.5 tw-rounded-lg tw-transition-all tw-duration-200',
                isActive(item.route)
                  ? 'tw-bg-primary-600 tw-text-white tw-font-medium tw-shadow-sm'
                  : 'tw-text-secondary-300 hover:tw-bg-secondary-700 hover:tw-text-white',
              ]"
              :title="props.collapsed && !props.mobile ? item.label : undefined"
              :data-testid="`nav-${item.label.toLowerCase()}`"
              @click="navigateTo(item.route)"
            >
              <span
                class="tw-flex tw-w-full tw-items-center tw-gap-3"
                :class="props.collapsed && !props.mobile ? 'tw-justify-center' : ''"
              >
                <AppIcon :name="item.icon" />
                <span
                  v-if="!props.collapsed || props.mobile"
                  class="tw-text-sm"
                >
                  {{ item.label }}
                </span>
              </span>
            </BaseButton>
          </li>
          <li v-if="showAdminLink">
            <a
              :href="config.adminUrl"
              target="_blank"
              rel="noopener noreferrer"
              :class="[
                'tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-rounded-lg tw-transition-all tw-duration-200',
                'tw-text-secondary-300 hover:tw-bg-secondary-700 hover:tw-text-white',
                props.collapsed && !props.mobile ? 'tw-justify-center' : '',
              ]"
              :title="props.collapsed && !props.mobile ? t('common.navigation.adminPanel') : undefined"
              data-testid="nav-admin-panel"
            >
              <AppIcon name="solar:shield-user-bold" />
              <span
                v-if="!props.collapsed || props.mobile"
                class="tw-text-sm"
              >
                {{ t('common.navigation.adminPanel') }}
              </span>
            </a>
          </li>
        </ul>
      </div>
    </nav>

    <!-- Footer with theme toggle, user info, logout -->
    <div
      class="tw-border-t tw-border-secondary-700"
      :class="props.collapsed && !props.mobile ? 'tw-p-2' : 'tw-p-4'"
    >
      <!-- Theme toggle and language switcher -->
      <div
        class="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mb-3"
        :class="props.collapsed && !props.mobile ? 'tw-flex-col' : ''"
      >
        <ThemeToggle
          size="sm"
          :title="props.collapsed && !props.mobile ? t('settings.theme.switchToDark') : undefined"
        />
        <LanguageSwitcher
          variant="sidebar"
          :show-label="!props.collapsed || props.mobile"
          data-testid="sidebar-language-switcher"
        />
      </div>

      <!-- User section -->
      <div
        v-if="authStore.isAuthenticated"
        class="tw-relative"
      >
        <BaseButton
          type="button"
          variant="bare"
          full-width
          class="tw-p-2 tw-rounded-lg tw-transition-colors hover:tw-bg-secondary-700"
          :aria-label="props.collapsed && !props.mobile ? t('common.navigation.userMenu') : undefined"
          :title="props.collapsed && !props.mobile ? authStore.user?.name || authStore.user?.email : undefined"
          data-testid="user-menu-button"
          @click="toggleUserMenu"
        >
          <span
            class="tw-flex tw-w-full tw-items-center tw-gap-3"
            :class="props.collapsed && !props.mobile ? 'tw-justify-center' : ''"
          >
            <span
              class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-primary-600 tw-text-white tw-flex tw-items-center tw-justify-center tw-font-medium tw-text-sm tw-flex-shrink-0"
              data-testid="user-avatar"
            >
              {{ getUserInitials() }}
            </span>
            <span
              v-if="!props.collapsed || props.mobile"
              class="tw-flex-1 tw-text-left tw-min-w-0"
            >
              <span class="tw-block tw-text-sm tw-font-medium tw-text-white tw-truncate">
                {{ authStore.user?.name || t('common.navigation.user') }}
              </span>
              <span class="tw-block tw-text-xs tw-text-secondary-400 tw-truncate">
                {{ authStore.user?.email }}
              </span>
            </span>
            <AppIcon
              v-if="!props.collapsed || props.mobile"
              name="solar:alt-arrow-up-bold"
              size="xs"
              class="tw-text-secondary-400 tw-transition-transform"
              :class="{ 'tw-rotate-180': userMenuOpen }"
            />
          </span>
        </BaseButton>

        <!-- User menu dropdown -->
        <Transition
          enter-active-class="tw-transition-all tw-duration-200 tw-ease-out"
          enter-from-class="tw-opacity-0 tw-scale-95 tw-translate-y-2"
          enter-to-class="tw-opacity-100 tw-scale-100 tw-translate-y-0"
          leave-active-class="tw-transition-all tw-duration-150 tw-ease-in"
          leave-from-class="tw-opacity-100 tw-scale-100 tw-translate-y-0"
          leave-to-class="tw-opacity-0 tw-scale-95 tw-translate-y-2"
        >
          <div
            v-if="userMenuOpen"
            :class="[
              'tw-absolute tw-bottom-full tw-mb-2 tw-w-48 tw-bg-secondary-700 tw-rounded-lg tw-shadow-lg tw-border tw-border-secondary-600 tw-z-50 tw-overflow-hidden',
              props.collapsed && !props.mobile ? 'tw-left-full tw-ml-2 tw-bottom-0 tw-mb-0' : 'tw-left-0',
            ]"
            data-testid="user-menu-dropdown"
          >
            <div class="tw-py-1">
              <BaseButton
                type="button"
                variant="bare"
                full-width
                class="tw-px-4 tw-py-2.5 tw-text-sm tw-transition-colors tw-text-secondary-200 hover:tw-bg-secondary-600"
                data-testid="user-menu-profile"
                @click="navigateTo(RouteName.Profile); userMenuOpen = false"
              >
                <span class="tw-flex tw-w-full tw-items-center tw-gap-3 tw-text-left">
                  <AppIcon name="solar:user-bold" />
                  {{ t('common.navigation.profile') }}
                </span>
              </BaseButton>
              <BaseButton
                type="button"
                variant="bare"
                full-width
                class="tw-px-4 tw-py-2.5 tw-text-sm tw-transition-colors tw-text-secondary-200 hover:tw-bg-secondary-600"
                data-testid="user-menu-settings"
                @click="navigateTo(RouteName.Settings); userMenuOpen = false"
              >
                <span class="tw-flex tw-w-full tw-items-center tw-gap-3 tw-text-left">
                  <AppIcon name="solar:settings-bold" />
                  {{ t('common.navigation.settings') }}
                </span>
              </BaseButton>
              <BaseButton
                type="button"
                variant="bare"
                full-width
                class="tw-px-4 tw-py-2.5 tw-text-sm tw-transition-colors tw-text-red-300 hover:tw-bg-red-900/30"
                data-testid="user-menu-logout"
                @click="handleLogout"
              >
                <span class="tw-flex tw-w-full tw-items-center tw-gap-3 tw-text-left">
                  <AppIcon name="solar:logout-2-bold" />
                  {{ t('common.navigation.logout') }}
                </span>
              </BaseButton>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Copyright -->
      <p
        v-if="!props.collapsed || props.mobile"
        class="tw-text-xs tw-text-secondary-400 tw-mt-3"
      >
        &copy; {{ currentYear }}
        <a
          :href="config.copyrightOwnerUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="tw-text-secondary-400 hover:tw-text-secondary-300 tw-transition-colors"
        >{{ config.copyrightOwner }}</a>. {{ t('common.footer.allRightsReserved') }}
      </p>
    </div>
  </aside>

  <!-- Overlay for mobile -->
  <Transition
    enter-active-class="tw-transition-opacity tw-duration-300"
    enter-from-class="tw-opacity-0"
    enter-to-class="tw-opacity-100"
    leave-active-class="tw-transition-opacity tw-duration-200"
    leave-from-class="tw-opacity-100"
    leave-to-class="tw-opacity-0"
  >
    <div
      v-if="props.mobile"
      class="tw-fixed tw-inset-0 tw-bg-secondary-900 tw-bg-opacity-50 tw-z-30"
      data-testid="sidebar-overlay"
      @click="emit('close')"
    />
  </Transition>
</template>
